import React, { useEffect, useMemo, useState } from 'react';

import QueryString from 'qs';
import { omit } from 'ramda';
import { useQuery, useQueryClient } from 'react-query';
import { useAsyncDebounce } from 'react-table';

import charitiesApi from 'api/charities';
import { FETCH_CHARITIES } from 'constants/queries';
import { Charity } from 'types/Charities';

import { CharitiesProps } from './Charities.props';
import CharitiesView from './Charities.view';

export const TABLE_OPTIONS = {
  page: 1,
  limit: 20,
};

const CharitiesContainer = (props: CharitiesProps): JSX.Element => {
  const [tableOptions, setTableOptions] =
    useState<Record<string, any>>(TABLE_OPTIONS);

  const queryClient = useQueryClient();

  const { isFetching, data, refetch } = useQuery(FETCH_CHARITIES, async () =>
    charitiesApi.getAll(
      `${
        props.location.search ? props.location.search + '&' : '?'
      }${QueryString.stringify(tableOptions)}`
    )
  );

  const queries = useMemo(
    () => QueryString.parse(new URLSearchParams(location.search).toString()),
    [location.search]
  );

  useEffect(() => {
    setTableOptions((opts) => ({ ...opts, page: 1 }));
  }, [queries.filter, queries.search]);

  const onSearchKey = useAsyncDebounce(
    (searchKey: string) =>
      props.history.push({
        pathname: props.location.pathname,
        search:
          '?' +
          new URLSearchParams({
            ...omit(['search'], queries),
            ...(searchKey ? { search: searchKey } : {}),
          }).toString(),
      }),
    600
  );

  const meta = React.useMemo(
    () =>
      data?.meta || {
        count: 0,
        limit: 10,
        page: 1,
      },
    [data]
  );

  const setCharity = (charity?: Charity) =>
    props.history.push({
      pathname: props.location.pathname,
      search:
        '?' +
        new URLSearchParams({
          ...omit(['id'], queries),
          ...(charity ? { id: charity.id } : {}),
        }).toString(),
    });

  useEffect(() => {
    queryClient.cancelQueries([FETCH_CHARITIES]);
    setTimeout(refetch, 300);
  }, [props.location.search, tableOptions]);

  return (
    <CharitiesView
      setCharity={setCharity}
      data={data?.data || []}
      meta={meta}
      onSearchKey={onSearchKey}
      isLoading={isFetching}
      tableOptions={tableOptions}
      setTableOptions={setTableOptions}
      refetch={refetch}
    />
  );
};

export default CharitiesContainer;
